import { useCreateEquipmentOption, useUpdateEquipmentOption, useGetEquipmentOptions } from "api/equipment/service";
import React, { useState, useMemo, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
    Card,
    CardBody,
    Row,
    Col,
    Button,
    Form,
    FormGroup,
    Label,
    Input,
    Spinner,
    CustomInput,
} from "reactstrap";
import classNames from "classnames";
import { getActiveColorStyle } from "../../../utils/colorUtils";
import { useTheme2FormPage, Theme2FormFieldsStyle, THEME2_ADMIN_INSETS } from "hooks/useTheme2FormPage";
import { useTranslation } from "react-i18next";
import SubHeader from "components/Navbars/SubHeader";

const EquipmentAdd = ({
    activeColor = "secondary",
    shellThemeVariant = "1",
    isLightTheme = false,
}) => {
    const { t } = useTranslation();
    const activeColorStyle = useMemo(() => getActiveColorStyle(activeColor), [activeColor]);
    const {
        isTheme2,
        inputStyle,
        inputClassName,
        theme2FieldsCss: baseTheme2FieldsCss,
        contentClassName,
        formFieldsClassName,
    } = useTheme2FormPage({
        shellThemeVariant,
        pageKey: "equipment-add",
        insetClass: THEME2_ADMIN_INSETS.inventoryParameters,
    });
    const [isMobile, setIsMobile] = useState(window.innerWidth < 992);

    const theme2FieldsCss = useMemo(
        () =>
            isTheme2
                ? `${baseTheme2FieldsCss}
                  @media (max-width: 991.98px) {
                    .sidebar.sidebar-shell-theme-2 ~ .main-panel > .content.equipment-add-page.equipment-add-page--theme2.theme2-admin-content-compact.theme2-inventory-parameters-inset {
                      padding-top: calc(74px + 45px) !important;
                    }
                  }
                  @media (max-width: 575.98px) {
                    .sidebar.sidebar-shell-theme-2 ~ .main-panel > .content.equipment-add-page.equipment-add-page--theme2.theme2-admin-content-compact.theme2-inventory-parameters-inset {
                      padding-top: calc(64px + 45px) !important;
                    }
                  }`
                : "",
        [isTheme2, baseTheme2FieldsCss]
    );

    const { id } = useParams();
    const isEditMode = Boolean(id);
    const navigate = useNavigate();
    const createEquipmentMutation = useCreateEquipmentOption();
    const updateEquipmentMutation = useUpdateEquipmentOption();
    const { data: equipmentData } = useGetEquipmentOptions(undefined, { enabled: isEditMode });

    const [formData, setFormData] = useState({
        id: '',
        documentId: '',
        name: '',
        is_active: true,
        options:[]
    });

    const [errors, setErrors] = useState({});

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 992);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const subHeaderTitle = useMemo(() => {
        if (isEditMode) {
            return formData.name || id;
        }
        return t("equipmentAdd.addNewEquipment");
    }, [isEditMode, formData.name, id, t]);

    const subHeaderStatus = useMemo(() => {
        if (!isEditMode) return "";
        return formData.is_active
            ? t("common.active")
            : t("common.inactive");
    }, [isEditMode, formData.is_active, t]);

    const subHeaderStatusColor = useMemo(() => {
        if (!isEditMode) return undefined;
        return formData.is_active ? "#28a745" : "#dc3545";
    }, [isEditMode, formData.is_active]);

    React.useEffect(() => {
        if (isEditMode && equipmentData && equipmentData.results) {
            const equipment = equipmentData.results.find(e => String(e.documentId) === String(id));
            if (equipment) {
                setFormData({
                    id: equipment.id,
                    documentId: equipment.documentId,
                    name: equipment.type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
                    is_active: equipment.option_status === "Active" ? true : false,
                    options: equipment.inventory_options || []
                });
            }
        }
    }, [isEditMode, equipmentData, id]);

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));

        // Clear error when user starts typing
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const addOption = () => {
        setFormData(prev => ({
            ...prev,
            options: [...prev.options, { name: '', value: '', isNew: true }]
        }));
    };

    const removeOption = (index) => {
        setFormData(prev => ({
            ...prev,
            options: prev.options.filter((_, i) => i !== index)
        }));
    };

    const updateOption = (index, field, value) => {
        setFormData(prev => ({
            ...prev,
            options: prev.options.map((option, i) => 
                i === index ? { ...option, [field]: value } : option
            )
        }));
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.name.trim()) {
            newErrors.name = t('equipmentAdd.equipmentNameRequired');
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };



    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        if (isEditMode) {
            updateEquipmentMutation.mutate({
                id: formData.documentId,
                data: {
                    type: formData.name.toLowerCase().replace(/\s+/g, '_'),
                    option_status: formData.is_active ? "Active" : "Passive",
                    options: formData.options.map(option => ({
                        name: option.name,
                        value: option.name.toLowerCase().replace(/\s+/g, '_'),
                        isNew: option.isNew
                    }))
                }
            });
        } else {
            createEquipmentMutation.mutate({
                type: formData.name.toLowerCase().replace(/\s+/g, '_'),
                option_status: formData.is_active ? "Active" : "Passive",
                options: formData.options.map(option => ({
                    name: option.name,
                    value: option.name.toLowerCase().replace(/\s+/g, '_'),
                    isNew: option.isNew
                }))
            });
        }
    };

    const handleCancel = () => {
        navigate('/inventory-parameters?tab=equipments');
    };

    return (
        <div
            className={contentClassName}
            style={{
                minHeight: "80vh",
                paddingTop: isMobile && !isTheme2 ? "7rem" : undefined,
            }}
        >
            <Theme2FormFieldsStyle css={theme2FieldsCss} />
            <div style={{ width: "100%" }}>
                <SubHeader
                    activeColor={activeColor}
                    back={-1}
                    backFallback="/inventory-parameters?tab=equipments"
                    title={subHeaderTitle}
                    status={subHeaderStatus}
                    statusColor={subHeaderStatusColor}
                    theme2ScrollGapFill={isTheme2}
                    isLightTheme={isLightTheme}
                />
            <Row>
                <Col md="12">
                    <Card>
                        <CardBody>
                            <Form onSubmit={handleSubmit} className={formFieldsClassName}>
                                <Row>
                                    <Col md="6">
                                        <FormGroup>
                                            <Label for="name" className="form-control-label">
                                                {t('equipmentAdd.equipmentName')} <span style={{ color: 'red' }}>*</span>
                                            </Label>
                                            <Input
                                                id="name"
                                                name="name"
                                                type="text"
                                                value={formData.name}
                                                onChange={handleInputChange}
                                                className={inputClassName(errors.name)}
                                                style={inputStyle}
                                                placeholder={t('equipmentAdd.enterEquipmentName')}
                                                disabled={createEquipmentMutation.isPending || updateEquipmentMutation.isPending}
                                            />
                                            {errors.name && (
                                                <div className="invalid-feedback d-block">
                                                    {errors.name}
                                                </div>
                                            )}
                                        </FormGroup>
                                    </Col>
                                </Row>


                                <Row>
                                    <Col md="12">
                                        <div className="d-flex justify-content-between align-items-center">
                                            <FormGroup className="mb-0">
                                                <CustomInput
                                                    type="switch"
                                                    id="is_active"
                                                    name="is_active"
                                                    label={formData.is_active ? t('equipmentAdd.active') : t('equipmentAdd.inactive')}
                                                    checked={formData.is_active}
                                                    onChange={handleInputChange}
                                                    disabled={createEquipmentMutation.isPending || updateEquipmentMutation.isPending}
                                                />
                                            </FormGroup>
                                        </div>
                                    </Col>
                                </Row>

                                <Row>
                                    <Col md="12">
                                        <FormGroup>
                                            <div className="d-flex justify-content-between align-items-center mb-3">
                                                <Label className="form-control-label">
                                                    {t('equipmentAdd.equipmentOptions')}
                                                </Label>
                                                <Button
                                                    color="info"
                                                    size="sm"
                                                    onClick={addOption}
                                                    disabled={createEquipmentMutation.isPending || updateEquipmentMutation.isPending}
                                                    style={{ ...activeColorStyle, color: 'white' }}
                                                >
                                                    <i className="tim-icons icon-simple-add" style={{ marginRight: '5px' }} />
                                                    {t('equipmentAdd.addOption')}
                                                </Button>
                                            </div>
                                            {formData.options.map((option, index) => (
                                                <div key={index} className="d-flex align-items-center mb-2">
                                                    <Input
                                                        type="text"
                                                        value={option.name}
                                                        onChange={(e) => updateOption(index, 'name', e.target.value)}
                                                        placeholder={t('equipmentAdd.optionName')}
                                                        className={classNames(inputClassName(), "me-2")}
                                                        style={inputStyle}
                                                        disabled={createEquipmentMutation.isPending || updateEquipmentMutation.isPending}
                                                    />
                                                    <Button
                                                        color="danger"
                                                        size="sm"
                                                        onClick={() => removeOption(index)}
                                                        disabled={createEquipmentMutation.isPending || updateEquipmentMutation.isPending}
                                                    >
                                                        <i className="tim-icons icon-simple-remove" />
                                                    </Button>
                                                </div>
                                            ))}
                                        </FormGroup>
                                    </Col>
                                </Row>

                                <div className="d-none d-lg-block mt-4" />
                            </Form>
                            <div className="d-flex flex-column flex-lg-row align-items-lg-center justify-content-end">
                                <div className="d-none d-lg-flex align-items-center">
                                    <Button
                                        color="secondary"
                                        outline
                                        size="sm"
                                        onClick={handleCancel}
                                        disabled={createEquipmentMutation.isPending || updateEquipmentMutation.isPending}
                                        className="mr-2"
                                    >
                                        {t("equipmentAdd.cancel")}
                                    </Button>
                                    <Button
                                        style={{ ...activeColorStyle, color: "white" }}
                                        size="sm"
                                        onClick={handleSubmit}
                                        disabled={createEquipmentMutation.isPending || updateEquipmentMutation.isPending}
                                    >
                                        {createEquipmentMutation.isPending || updateEquipmentMutation.isPending ? (
                                            <>
                                                <Spinner size="sm" className="mr-1" />
                                                {isEditMode
                                                    ? t("equipmentAdd.updating")
                                                    : t("equipmentAdd.creating")}
                                            </>
                                        ) : (
                                            <>
                                                <i
                                                    className={
                                                        isEditMode
                                                            ? "tim-icons icon-pencil"
                                                            : "tim-icons icon-simple-add"
                                                    }
                                                    style={{ marginRight: "8px" }}
                                                />
                                                {isEditMode
                                                    ? t("equipmentAdd.updateEquipment")
                                                    : t("equipmentAdd.createEquipment")}
                                            </>
                                        )}
                                    </Button>
                                </div>

                                <div className="d-flex d-lg-none flex-column w-100 mt-3">
                                    <Button
                                        color="secondary"
                                        outline
                                        onClick={handleCancel}
                                        disabled={createEquipmentMutation.isPending || updateEquipmentMutation.isPending}
                                        className="mb-2 w-100"
                                        style={{ padding: "12px" }}
                                    >
                                        {t("equipmentAdd.cancel")}
                                    </Button>
                                    <Button
                                        style={{ ...activeColorStyle, color: "white", padding: "12px" }}
                                        onClick={handleSubmit}
                                        disabled={createEquipmentMutation.isPending || updateEquipmentMutation.isPending}
                                        className="w-100"
                                    >
                                        {createEquipmentMutation.isPending || updateEquipmentMutation.isPending ? (
                                            <>
                                                <Spinner size="sm" className="mr-1" />
                                                {isEditMode
                                                    ? t("equipmentAdd.updating")
                                                    : t("equipmentAdd.creating")}
                                            </>
                                        ) : (
                                            <>
                                                <i
                                                    className={
                                                        isEditMode
                                                            ? "tim-icons icon-pencil"
                                                            : "tim-icons icon-simple-add"
                                                    }
                                                    style={{ marginRight: "8px" }}
                                                />
                                                {isEditMode
                                                    ? t("equipmentAdd.updateEquipment")
                                                    : t("equipmentAdd.createEquipment")}
                                            </>
                                        )}
                                    </Button>
                                </div>
                            </div>
                        </CardBody>
                    </Card>
                </Col>
            </Row>
            </div>
        </div>
    );
};

export default EquipmentAdd; 